/**
 * IngestionDiagnosticsPanel — surface the trainer's recent batches, what
 * landed, what didn't, and what is sitting in the review queue.
 *
 * Answers the user's question from the live test: was the box/list scan
 * ingested, or only the deep (live-scan) detail rows? The panel groups
 * recent batches by source, flags failed ingest attempts, and shows the
 * "ingest health" verdict from `deriveIngestHealth`.
 *
 * Read-only and production-safe — it only renders data the backend
 * already exposes via /api/dex/batches and /api/dex/review-queue.
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  deriveIngestHealth,
  describeHealth,
  summarizeBatches,
  summarizeReviewQueue,
  type BatchLike,
  type ReviewQueueItemLike,
} from "@/lib/ingestionDiagnostics";
import { listStoredSprites, storedSpriteBytes } from "@/lib/localSpriteStore";

interface BatchesResponse {
  batches?: ReadonlyArray<BatchLike>;
  stats?: {
    total?: number;
    totalRecords?: number;
    shinies?: number;
    hundos?: number;
    lucky?: number;
    lastUploadAt?: string | null;
  };
}

interface ReviewQueueResponse {
  items?: ReadonlyArray<ReviewQueueItemLike>;
}

function formatRelative(ts: number | null): string {
  if (!ts) return "-";
  const diff = Date.now() - ts;
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

export function IngestionDiagnosticsPanel() {
  const batchesQ = useQuery<BatchesResponse>({
    queryKey: ["/api/dex/batches"],
    staleTime: 30_000,
  });
  const reviewQ = useQuery<ReviewQueueResponse>({
    queryKey: ["/api/dex/review-queue"],
    staleTime: 30_000,
  });

  const summary = useMemo(
    () => summarizeBatches(batchesQ.data?.batches),
    [batchesQ.data?.batches],
  );
  const review = useMemo(
    () => summarizeReviewQueue(reviewQ.data?.items),
    [reviewQ.data?.items],
  );
  const health = deriveIngestHealth(summary);
  const healthDescription = describeHealth(health);

  // Local sprite cache (PR #114). Cheap to compute — read once per render
  // from the index that localSpriteStore already keeps in localStorage.
  const spriteStats = useMemo(() => {
    try {
      const entries = listStoredSprites();
      return { count: entries.length, bytes: storedSpriteBytes() };
    } catch {
      return { count: 0, bytes: 0 };
    }
  }, [batchesQ.data, reviewQ.data]);

  const healthTone = (() => {
    if (health === "ok") return { cls: "ingest-stat-value--ok", border: "hsl(142 80% 35% / 0.5)" };
    if (health === "stalled") return { cls: "ingest-failure-reason", border: "hsl(354 78% 56% / 0.45)" };
    if (health === "no_data") return { cls: "ingest-stat-label", border: "hsl(142 30% 22%)" };
    return { cls: "ingest-stat-value--warn", border: "hsl(43 96% 60% / 0.45)" };
  })();

  function copyJson() {
    const payload = {
      generatedAt: new Date().toISOString(),
      health,
      summary,
      review,
      batches: batchesQ.data?.batches ?? [],
    };
    try {
      navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    } catch {
      // Web preview may not have clipboard. The rendered JSON below is
      // long-press copyable.
    }
  }

  return (
    <div
      className="rounded-2xl p-4 mb-3"
      style={{ background: "var(--surface-elev-1)", border: "1px solid var(--surface-elev-border)" }}
      data-testid="settings-ingestion-diagnostics"
    >
      <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-2">
        Ingestion diagnostics
      </p>
      <p
        className={`${healthTone.cls} text-[11px] leading-relaxed mb-3`}
        data-testid="ingest-health-summary"
        data-health={health}
      >
        {healthDescription}
      </p>

      <div className="grid grid-cols-2 gap-2 mb-3" data-testid="ingest-counters">
        <Stat label="Batches" value={`${summary.acceptedBatches} / ${summary.totalBatches}`} hint="accepted / total" />
        <Stat label="Records" value={summary.totalAcceptedRows.toString()} hint="rows accepted" />
        <Stat
          label="Top-line"
          value={summary.topLineIngested ? "Yes" : "No"}
          hint="box / list / pokedex / journal"
          tone={summary.topLineIngested ? "ok" : "warn"}
        />
        <Stat
          label="Deep"
          value={summary.deepIngested ? "Yes" : "No"}
          hint="detail / live / video"
          tone={summary.deepIngested ? "ok" : "warn"}
        />
      </div>

      {summary.bySource.length > 0 && (
        <div className="mb-3" data-testid="ingest-by-source">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">By source</p>
          <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${healthTone.border}` }}>
            {summary.bySource.map((b) => (
              <div
                key={b.source}
                className="flex items-center gap-2 px-3 py-2 text-[11px] border-b last:border-b-0"
                style={{ borderColor: "var(--surface-elev-border)" }}
                data-testid={`ingest-source-${b.source}`}
              >
                <span className="font-mono">{b.source}</span>
                <span className="ingest-source-tier text-[9px] uppercase tracking-wider px-1.5 rounded">
                  {b.tier}
                </span>
                <span className="ingest-source-metrics ml-auto tabular-nums">
                  {b.acceptedRows} rows · {b.batches} batches
                  {b.failedBatches > 0 ? (
                    <span className="ingest-source-failed"> · {b.failedBatches} failed</span>
                  ) : null}
                </span>
                <span className="ingest-source-relative text-[10px]">
                  {formatRelative(b.lastUploadedAt)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {summary.recentFailures.length > 0 && (
        <div className="mb-3" data-testid="ingest-failures">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1">
            Recent failures ({summary.recentFailures.length})
          </p>
          <div className="rounded-xl overflow-hidden" style={{ border: "1px solid hsl(354 78% 56% / 0.32)" }}>
            {summary.recentFailures.map((b) => (
              <div
                key={`fail-${b.id ?? b.uploadedAt}`}
                className="flex items-center gap-2 px-3 py-2 text-[11px] border-b last:border-b-0"
                style={{ borderColor: "hsl(354 78% 56% / 0.18)" }}
              >
                <span className="font-mono">{b.source ?? "(unknown)"}</span>
                <span className="ingest-failure-reason ml-auto">
                  {b.failureReason ?? "failed"}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2 mb-3" data-testid="review-counters">
        <Stat label="Review queue" value={review.total.toString()} hint="awaiting confirm" />
        <Stat label="Low confidence" value={review.lowConfidenceCount.toString()} hint="confidence < 0.5" />
      </div>

      <div className="grid grid-cols-2 gap-2 mb-3" data-testid="sprite-counters">
        <Stat
          label="Local sprites"
          value={spriteStats.count.toString()}
          hint="cached on this device"
        />
        <Stat
          label="Sprite cache"
          value={spriteStats.bytes > 0 ? `${Math.round(spriteStats.bytes / 1024)} KB` : "0 KB"}
          hint="approx local storage"
        />
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => { batchesQ.refetch(); reviewQ.refetch(); }}
          data-testid="button-ingest-diag-refresh"
          className="rounded-xl px-3 py-1.5 text-[11px] font-semibold"
          style={{
            background: "var(--surface-elev-2)",
            border: "1px solid var(--surface-elev-border)",
            color: "hsl(var(--foreground))",
          }}
        >
          Refresh
        </button>
        <button
          type="button"
          onClick={copyJson}
          data-testid="button-ingest-diag-copy"
          className="rounded-xl px-3 py-1.5 text-[11px] font-semibold"
          style={{
            background: "var(--surface-elev-2)",
            border: "1px solid var(--surface-elev-border)",
            color: "hsl(var(--foreground))",
          }}
        >
          Copy JSON
        </button>
      </div>
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "ok" | "warn";
}) {
  const valueCls =
    tone === "warn"
      ? "ingest-stat-value--warn"
      : tone === "ok"
        ? "ingest-stat-value--ok"
        : "ingest-stat-value";
  return (
    <div
      className="rounded-xl px-3 py-2"
      style={{ background: "var(--surface-elev-2)", border: "1px solid var(--surface-elev-border)" }}
    >
      <p className={`ingest-stat-label text-[9px] font-semibold uppercase tracking-wider`}>{label}</p>
      <p className={`${valueCls} font-mono text-sm font-bold mt-0.5 tabular-nums`}>{value}</p>
      {hint ? <p className="ingest-stat-hint text-[9px] mt-0.5">{hint}</p> : null}
    </div>
  );
}
